import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MoreHorizontal,
  Trash2,
  Share,
  Copy,
  Loader2,
  Star,
  StarOff,
  Image as ImageIcon,
  Smile,
  ChevronRight,
  Check,
  Save,
} from 'lucide-react';
import { useDocumentStore, useAuthStore } from '../lib/store';
import { documentsApi } from '../lib/api';
import Editor from '../components/Editor';
import CollaboratorsList from '../components/CollaboratorsList';
import { cn } from '../lib/utils';

// Emoji picker data (subset)
const EMOJI_LIST = ['📄', '📝', '📚', '📖', '💡', '🎯', '🚀', '✨', '📌', '🔖', '📁', '💼', '🏠', '🌟', '💭', '📊'];

export default function Document() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { documents, updateDocument, deleteDocument, addDocument, setActiveDocument } = useDocumentStore();
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState<'editor' | 'viewer'>('viewer');
  const [shareStatus, setShareStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [shareMessage, setShareMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showCoverInput, setShowCoverInput] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const document = documents.find((doc) => doc.id === id);
  const parentDoc = document?.parentId ? documents.find(d => d.id === document.parentId) : null;

  useEffect(() => {
    if (id) setActiveDocument(id);
    return () => setActiveDocument(null);
  }, [id, setActiveDocument]);

  useEffect(() => {
    const fetchDocument = async () => {
      if (!id) return;
      setIsLoading(true);

      try {
        const response = await documentsApi.get(id);
        updateDocument(id, response.document);
      } catch {
        // Create local document if not exists
        if (!documents.find(d => d.id === id)) {
          addDocument({
            id,
            title: '',
            parentId: null,
            ownerId: user?.id || 'local',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            isArchived: false,
            isFavorited: false,
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [id, addDocument, updateDocument, user?.id]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!id) return;
    const newTitle = e.target.value;
    updateDocument(id, { title: newTitle, updatedAt: new Date().toISOString() });
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounced API update
    setSaveStatus('saving');
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await documentsApi.update(id, { title: newTitle });
        setSaveStatus('saved');
        // Reset to idle after 2 seconds
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch {
        setSaveStatus('idle');
      }
    }, 800); // 800ms debounce
  };

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await documentsApi.delete(id);
    } catch {
      // Handle locally
    }
    deleteDocument(id);
    navigate('/');
  };

  const handleToggleFavorite = () => {
    if (!id || !document) return;
    const newValue = !document.isFavorited;
    updateDocument(id, { isFavorited: newValue });
    documentsApi.update(id, { isFavorited: newValue }).catch(() => {});
  };

  const handleDuplicate = async () => {
    if (!id || !document) return;
    const title = document.title || t('document.untitled') || 'Untitled';
    const copyTitle = `${t('document.copyOf') || 'Copy of'} ${title}`;

    try {
      const created = await documentsApi.create({ title: copyTitle, parentId: document.parentId, icon: document.icon });
      const newId = created.document.id;
      addDocument({ ...created.document, coverImage: document.coverImage, content: document.content });
      await documentsApi.update(newId, {
        content: document.content,
        coverImage: document.coverImage,
        isFavorited: false,
      });
      navigate(`/app/document/${newId}`);
    } catch (err) {
      console.error('Duplicate failed', err);
    } finally {
      setShowMenu(false);
    }
  };

  const handleSetIcon = (emoji: string) => {
    if (!id) return;
    updateDocument(id, { icon: emoji });
    documentsApi.update(id, { icon: emoji }).catch(() => {});
    setShowEmojiPicker(false);
  };

  const handleSetCover = (url: string) => {
    if (!id) return;
    updateDocument(id, { coverImage: url });
    documentsApi.update(id, { coverImage: url }).catch(() => {});
    setShowCoverInput(false);
  };

  const handleShareSubmit = async () => {
    if (!id || !shareEmail.trim()) return;
    setShareStatus('sending');
    setShareMessage('');
    try {
      await documentsApi.share(id, shareEmail.trim(), sharePermission);
      setShareStatus('success');
      setShareMessage(t('share.success') || 'Shared successfully');
      setShareEmail('');
    } catch (err) {
      console.error('Share failed', err);
      setShareStatus('error');
      setShareMessage(t('share.error') || 'Unable to share. Check the email and try again.');
    }
  };

  if (isLoading && !document) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!document) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-lg text-muted-foreground">{t('errors.notFound')}</p>
        <button onClick={() => navigate('/')} className="btn-primary">
          {t('nav.home')}
        </button>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {showShare && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={() => setShowShare(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="w-full max-w-lg rounded-xl border bg-card shadow-2xl p-5">
              <CollaboratorsList
                documentId={id || ''}
                currentUserId={user?.id}
                isOwner={document.ownerId === user?.id}
                onClose={() => setShowShare(false)}
              />
            </div>
          </div>
        </>
      )}

      {/* Cover Image */}
      {document.coverImage ? (
        <div className="cover-image group">
          <img src={document.coverImage} alt="" />
                <button
                  onClick={() => handleSetCover('')}
                  className="absolute top-2 end-2 btn-ghost bg-background/80 opacity-0 group-hover:opacity-100"
                >
                  {t('actions.delete')}
                </button>
        </div>
      ) : (
        <div className="h-12" />
      )}

      <div className="notion-container">
        {/* Icon and Title */}
        <div className="relative mb-4">
          {/* Page Icon */}
          <div className="relative inline-block">
            {document.icon ? (
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="page-icon hover:ring-2 hover:ring-primary/20"
              >
                {document.icon}
              </button>
            ) : (
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setShowEmojiPicker(true)}
                  className="btn-ghost text-muted-foreground"
                >
                  <Smile className="h-4 w-4 me-1" />
                  {t('document.addIcon') || 'Add icon'}
                </button>
                {!document.coverImage && (
                  <button
                    onClick={() => setShowCoverInput(true)}
                    className="btn-ghost text-muted-foreground"
                  >
                    <ImageIcon className="h-4 w-4 me-1" />
                    {t('document.addCover') || 'Add cover'}
                  </button>
                )}
              </div>
            )}

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowEmojiPicker(false)} />
                <div className="dropdown-content absolute start-0 top-full mt-2 p-2 w-64 z-50">
                  <div className="grid grid-cols-8 gap-1">
                    {EMOJI_LIST.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => handleSetIcon(emoji)}
                        className="h-8 w-8 rounded hover:bg-secondary flex items-center justify-center text-lg"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                  {document.icon && (
                    <button
                      onClick={() => handleSetIcon('')}
                      className="mt-2 w-full text-xs text-muted-foreground hover:text-destructive"
                    >
                      {t('document.removeIcon') || 'Remove icon'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Title */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 flex items-center gap-3">
              <input
                type="text"
                value={document.title}
                onChange={handleTitleChange}
                placeholder={t('document.untitled')}
                className={cn(
                  'flex-1 bg-transparent text-4xl font-bold tracking-tight outline-none w-full',
                  'placeholder:text-muted-foreground/50',
                  i18n.language === 'ar' && 'font-cairo text-right leading-relaxed pb-2'
                )}
                dir="auto"
                style={{ 
                  fontFamily: i18n.language === 'ar' ? 'Cairo, Tajawal, sans-serif' : undefined,
                  lineHeight: i18n.language === 'ar' ? '1.5' : undefined
                }}
              />
              {/* Save Status */}
              {saveStatus !== 'idle' && (
                <div className={cn(
                  'shrink-0 flex items-center gap-1 px-2 py-1 rounded text-xs',
                  saveStatus === 'saving' && 'text-blue-500',
                  saveStatus === 'saved' && 'text-green-500'
                )}>
                  {saveStatus === 'saving' ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>{t('status.saving') || 'Saving...'}</span>
                    </>
                  ) : (
                    <>
                      <Check className="h-3 w-3" />
                      <span>{t('status.saved') || 'Saved'}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Actions Menu */}
            <div className="relative shrink-0">
              <div className="flex items-center gap-1">
                <button
                  onClick={handleToggleFavorite}
                  className={cn('btn-icon', document.isFavorited && 'text-yellow-500')}
                  title={document.isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {document.isFavorited ? (
                    <Star className="h-4 w-4 fill-current" />
                  ) : (

                    <StarOff className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="btn-icon"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </div>

              {showMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                  <div className="dropdown-content absolute end-0 top-full mt-1 w-48">
                    <button
                      className="menu-item"
                      onClick={() => {
                        setShowShare(true);
                        setShowMenu(false);
                      }}
                    >
                      <Share className="h-4 w-4" />
                      {t('document.share') || 'Share'}
                    </button>
                    <button className="menu-item" onClick={handleDuplicate}>
                      <Copy className="h-4 w-4" />
                      {t('document.duplicate')}
                    </button>
                    <hr className="my-1 border-border" />
                    <button
                      className="menu-item text-destructive hover:bg-destructive/10"
                      onClick={handleDelete}
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('document.delete')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Breadcrumb for nested docs */}
          {parentDoc && (
            <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
              <button
                onClick={() => navigate(`/app/document/${parentDoc.id}`)}
                className="hover:text-foreground transition-colors"
              >
                {parentDoc.icon && <span className="me-1">{parentDoc.icon}</span>}
                {parentDoc.title || t('document.untitled')}
              </button>
              <ChevronRight className="h-3 w-3 flip-rtl" />
              <span className="text-foreground">
                {document.title || t('document.untitled')}
              </span>
            </div>
          )}
        </div>

        {/* Cover URL input */}
        {showCoverInput && (
          <div className="mb-4 flex gap-2">
            <input
              type="url"
              placeholder="https://..."
              className="input flex-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSetCover((e.target as HTMLInputElement).value);
                }
              }}
            />
            <button onClick={() => setShowCoverInput(false)} className="btn-ghost">
              {t('actions.cancel')}
            </button>
          </div>
        )}

        {/* Editor with real-time collaboration */}
        <Editor
          documentId={id!}
          initialContent={document.content}
          collaborative={true}
          onChange={(content: string) => {
            updateDocument(id!, {
              content,
              updatedAt: new Date().toISOString(),
            });
          }}
        />
      </div>
    </div>
  );
}
